import React, { ReactNode } from 'react';

declare const django: {
    gettext: (value: string) => string;
    pgettext: (context: string, value: string) => string;
    ngettext: (singular: string, plural: string, count: number) => string;
    npgettext: (
        context: string,
        singular: string,
        plural: string,
        count: number,
    ) => string;
    interpolate: (
        formats: string,
        obj: Record<string, unknown> | unknown[],
        named?: boolean,
    ) => string;
};

declare global {
    interface Window {
        django: typeof django;
    }
}

/**
 * Converts newlines in text to <br /> elements
 */
export default function nl2br(text: string): ReactNode[] {
    const res: ReactNode[] = [];
    text.split('\n').forEach((x, i) => {
        if (i !== 0) {
            res.push(<br key={`br-${i}`} />);
        }
        res.push(x);
    });
    return res;
}

if (
    typeof window !== 'undefined' &&
    (!window.django || !window.django.gettext)
) {
    const docsUrl =
        'https://docs.djangoproject.com/en/2.2/topics/i18n/translation/#module-django.views.i18n';
    // eslint-disable-next-line no-console
    console.error(
        'Did not find window.gettext - is Django JavascriptCatalog installed correctly?',
    );
    // eslint-disable-next-line no-console
    console.error(`  see more at ${docsUrl}`);
}

export const gettext = (value: string): string => django.gettext(value);

export const pgettext = (context: string, value: string): string =>
    django.pgettext(context, value);

export const ngettext = (
    singular: string,
    plural: string,
    count: number,
): string => django.ngettext(singular, plural, count);

export const npgettext = (
    context: string,
    singular: string,
    plural: string,
    count: number,
): string => django.npgettext(context, singular, plural, count);

/**
 * Dynamically populates a format string
 *
 * @example
 * const fmts = ngettext('There is %s object', 'There are %s objects', 11);
 * interpolate(fmts, [11]) // 'There are 11 objects'
 *
 * @example
 * const d = { count: 10, total: 50 };
 * const fmts = ngettext('%(count)s of %(total)s', '%(count)s of %(total)s', d.count);
 * interpolate(fmts, d, true) // '10 of 50'
 */
export const interpolate = (
    formats: string,
    obj: Record<string, unknown> | unknown[],
    named?: boolean,
): string => django.interpolate(formats, obj, named);
